import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { taskTags } from '@shared/task-tags';
import { GitPullRequest, Hash, Hourglass, Link2, Plus, Tag, UserPlus, X } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { TaskBoard, TaskContextRef, TaskDetail, TaskUpdateInput } from '@shared/types/tasks';
import { addTaskContexts, removeTaskContext } from '@/lib/tasks-api';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { AssigneePicker, ContextPicker, LabelPicker } from './TaskPickers';
import { ContextChip, LabelChip, memberHandle, memberName } from './task-ui';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { taskPerson } from './task-people';
import { KeepSuggestions, KindSelect, PrList, SizeSelect, TypePicker } from './TaskTypeFields';
import { TASK_FIELD_LABELS, taskTypeOf } from './task-types';
import { PrioritySelect } from './TaskPriority';
import { WaitingChip, WaitingPicker } from './WaitingOn';
import { AiMachineChip, type AiMachineChoice } from './AiMachineItems';
import { useAiMachines } from './use-ai-machines';
import { PickupSkipNote } from './PickupSkip';
import { asWaitingRef, pickupSkipOf, waitingKey } from './task-waiting';
import { useBoardPath } from '@/navigation/project-paths';
import { StackedOn } from './StackedOn';
import { SubtaskOf } from './SubtaskOf';
import { usePermission } from '@/hooks/usePermission';
import { TASK_SOURCE_LABELS } from '@shared/types/task-sources';

type BoardMeta = Pick<TaskBoard, 'members' | 'labels' | 'columns' | 'types'>;

/**
 * One property: its icon and its chips, in the same row as the type and who's assigned. The
 * icon says what it is (its name on hover) and, for the ones you pick, opens the picker. No title
 * above it, so adding one from the + menu doesn't push its picker onto a new line.
 */
function Property({ marker, children }: { marker: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 max-w-full items-center gap-0.5">
      {marker}
      {/* A long linked name is cut short (full name on hover), so the row stays tidy. */}
      <div className="flex min-w-0 flex-wrap items-center gap-1.5 [&>span]:max-w-56">{children}</div>
    </div>
  );
}

/** A small "+" (or "+ Assign", or a property's icon) that opens a property's picker. */
function AddButton({ label, showLabel, icon, ...props }: { label: string; showLabel: boolean; icon?: ReactNode } & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-md text-sm text-muted-foreground hover:bg-background hover:text-foreground [&_svg]:size-3.5',
        // On the page (not in the grey box), a grey hover shows.
        '[[data-inline]_&]:hover:bg-muted',
        showLabel ? 'px-1.5' : 'w-7 justify-center',
        // Phone-sized touch targets (forms-and-inputs.project.mdc, "Phone pages").
        showLabel ? 'max-md:h-11' : 'max-md:size-11'
      )}
      {...props}
    >
      {icon ?? <Plus className="size-3.5" />}
      {showLabel ? label : null}
    </button>
  );
}

/** What the + menu can add: each opens that property's picker. */
type Adding = 'waiting' | 'prs' | 'label' | 'link';

/**
 * A task's properties, kept short, in one row with no titles: its type, Size and
 * Kind pills (when the type has them), who's assigned, then only what the task has (Waiting on,
 * pull requests, labels, tags and links, each marked by an icon), and one + that asks what to add.
 * `inline`: under the title on the page (the Board pop-up on desktop); otherwise in a grey box.
 */
export function TaskProperties({
  task,
  meta,
  patch,
  run,
  currentPage,
  onNavigate,
  inline = false,
  className,
}: {
  task: TaskDetail;
  meta: BoardMeta;
  patch: (input: TaskUpdateInput, optimistic?: Partial<TaskDetail>) => Promise<boolean>;
  run: (request: Promise<{ error?: string }>, optimistic?: Partial<TaskDetail>) => Promise<boolean>;
  currentPage?: (TaskContextRef & { title: string }) | null;
  onNavigate?: () => void;
  /** On the page under the title (desktop pop-up); phones always get the grey box. */
  inline?: boolean;
  className?: string;
}) {
  // The grey box on phones (md and up is the two-pane layout, the same breakpoint).
  const isMobile = useIsMobile();
  const boardPath = useBoardPath();
  const onPage = inline && !isMobile;
  // The property picked from the + menu, shown (with its picker open) until the picker closes.
  const [adding, setAdding] = useState<Adding | null>(null);
  // Picked in the + menu, shown once the menu has closed: a picker opened while the menu is still
  // fading out sees the focus there and closes at once.
  const [picked, setPicked] = useState<Adding | null>(null);
  // Straight from the text, so a tag shows the moment it's typed and saved (the card's come from the server).
  const tags = taskTags([task.title, task.description, ...task.events.filter((e) => e.kind === 'chat').map((e) => e.body)]);
  // The type's own fields; a field it doesn't show keeps its value, hidden.
  const fields = taskTypeOf(meta.types, task)?.fields ?? [];
  const setWaiting = (next: TaskDetail['waiting_on']) => void patch({ waiting_on: next.map(asWaitingRef) }, { waiting_on: next });
  // Assigning the AI builder (T-2) is Waiting on: AI builder, which the factory picks up as whoever
  // asked; only people who may start runs get it in Assign (the server checks when it picks up).
  const canRun = usePermission('RUNS_START');
  const aiOn = task.waiting_on.some((w) => w.target === 'ai');
  const pickupSkip = pickupSkipOf(task.waiting_on);
  const toggleAi = () => setWaiting(aiOn ? task.waiting_on.filter((w) => w.target !== 'ai') : [...task.waiting_on, { target: 'ai' }]);
  // SF-T-174: the AI builder on a chosen machine, from Assign or Waiting on: picking one asks it too. The
  // machines load when a picker first opens.
  const [wantMachines, setWantMachines] = useState(false);
  const { machines, failed: machinesFailed, retry: retryMachines } = useAiMachines(wantMachines);
  const pickAiMachine = (machineId: string | null) => {
    const waiting: TaskDetail['waiting_on'] = aiOn ? task.waiting_on : [...task.waiting_on, { target: 'ai' }];
    const machine = machineId ? (machines?.find((m) => m.id === machineId) ?? (task.ai_machine?.id === machineId ? task.ai_machine : null)) : null;
    void patch({ ai_machine_id: machineId, ...(aiOn ? {} : { waiting_on: waiting.map(asWaitingRef) }) }, { ai_machine: machine, waiting_on: waiting });
  };
  const aiMachine: AiMachineChoice | undefined = canRun
    ? { machines, selected: task.ai_machine, onPick: pickAiMachine, load: () => setWantMachines(true), failed: machinesFailed, retry: retryMachines }
    : undefined;
  const chipBg = onPage ? 'bg-muted' : 'bg-background';
  /**
   * The picker opened from the menu (open as it appears); closing it hides the property again if
   * nothing was added. Opened from its icon instead, it needs nothing.
   */
  const picking = (what: Adding) =>
    adding === what
      ? {
          defaultOpen: true,
          onOpenChange: (open: boolean) => {
            if (!open) setAdding((current) => (current === what ? null : current));
          },
        }
      : {};

  const menu: Array<{ what: Adding; label: string; icon: ReactNode }> = [
    { what: 'waiting', label: 'Wait on someone', icon: <Hourglass /> },
    ...(fields.includes('prs') ? [{ what: 'prs' as const, label: 'Pull request', icon: <GitPullRequest /> }] : []),
    { what: 'label', label: 'Label', icon: <Tag /> },
    { what: 'link', label: 'Link something', icon: <Link2 /> },
  ];

  const has: Record<Adding, boolean> = {
    waiting: task.waiting_on.length > 0,
    prs: task.prs.length > 0,
    label: task.label_ids.length > 0,
    link: task.contexts.length > 0,
  };
  // Once a property shows, its icon opens its picker, so the + only offers the ones not there yet.
  const addable = menu.filter((item) => !has[item.what]);
  const showWaiting = has.waiting || adding === 'waiting';
  const showPrs = fields.includes('prs') && (has.prs || adding === 'prs');
  const showLabels = has.label || adding === 'label';
  const showLinks = has.link || adding === 'link';
  /** The property's icon, as the button that opens its picker. */
  const iconButton = (title: string, icon: ReactNode) => <AddButton label={title} showLabel={false} icon={icon} data-hint={title} />;

  return (
    <div className={cn(!onPage && 'rounded-lg bg-muted/50 px-3 py-2.5', className)}>
      {/* One row: type, size, kind, priority and who, whose pills say what they are, then what else the
          task has, each marked by an icon, then the + that adds more. */}
      <div className="flex flex-wrap items-center gap-1.5" data-inline={onPage || undefined}>
        {meta.types.length ? <TypePicker task={task} types={meta.types} patch={patch} /> : null}
        {fields.includes('size') ? <SizeSelect task={task} patch={patch} /> : null}
        {fields.includes('kind') ? <KindSelect task={task} patch={patch} /> : null}
        {fields.includes('priority') ? <PrioritySelect task={task} patch={patch} /> : null}
        {/* Jev's picks (T-17), confirmed in one go. */}
        <KeepSuggestions task={task} patch={patch} fields={[...(meta.types.length > 1 ? (['type'] as const) : []), ...fields.filter((f): f is 'size' | 'kind' => f === 'size' || f === 'kind')]} />
        {task.assignee_ids.map((id) => {
          const member = taskPerson(meta.members, id);
          const name = memberName(member);
          const remaining = task.assignee_ids.filter((x) => x !== id);
          return (
            <span key={id} data-hint={`Assigned to ${name}`} className={cn('inline-flex h-7 items-center gap-1.5 rounded-full pl-0.5 pr-1 text-sm', chipBg)}>
              <MemberAvatar name={name} photourl={member?.photourl} size="sm" hint={false} />
              {/* The handle keeps the chip short; the full name is on hover. */}
              <span className="max-w-[10rem] truncate">{memberHandle(member)}</span>
              <button
                type="button"
                aria-label={`Unassign ${name}`}
                className="flex size-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => void patch({ assignee_ids: remaining }, { assignee_ids: remaining })}
              >
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        {/* "Assign" when no one is; then a person-plus beside them, apart from the + menu. */}
        <AssigneePicker
          members={meta.members}
          selected={task.assignee_ids}
          onChange={(ids) => void patch({ assignee_ids: ids }, { assignee_ids: ids })}
          aiBuilder={canRun ? { on: aiOn, onToggle: toggleAi, machine: aiMachine } : undefined}
        >
          <AddButton
            label={task.assignee_ids.length || aiOn ? 'Change who is assigned' : 'Assign'}
            showLabel={!task.assignee_ids.length && !aiOn}
            icon={task.assignee_ids.length || aiOn ? <UserPlus className="size-3.5" /> : undefined}
          />
        </AssigneePicker>
        {/* Whose turn it is: someone to answer a question, a task to finish first, or the AI builder. */}
        {showWaiting ? (
          <Property
            marker={
              <WaitingPicker
                members={meta.members}
                // Waiting on the AI builder: the factory picks the task up and starts a run (T-2).
                aiBuilder
                aiMachine={aiMachine}
                // T-58: a task mirrored from an enabled source can wait on it.
                upstream={task.upstream?.enabled ? TASK_SOURCE_LABELS[task.upstream.kind] : undefined}
                selected={task.waiting_on}
                taskId={task.id}
                onChange={setWaiting}
                {...picking('waiting')}
              >
                {iconButton('Waiting on', <Hourglass />)}
              </WaitingPicker>
            }
          >
            {task.waiting_on.map((w) => (
              <WaitingChip
                key={waitingKey(w)}
                waiting={w}
                members={meta.members}
                onNavigate={onNavigate}
                className={chipBg}
                onRemove={() => setWaiting(task.waiting_on.filter((x) => waitingKey(x) !== waitingKey(w)))}
              />
            ))}
          </Property>
        ) : null}
        {/* SF-T-174: the machine chosen for the AI builder's runs; it stays until someone changes it. */}
        {task.ai_machine ? (
          <AiMachineChip
            machine={task.ai_machine}
            onClear={canRun ? () => void patch({ ai_machine_id: null }, { ai_machine: null }) : undefined}
          />
        ) : null}
        {/* T-65: why the AI builder hasn't picked it up yet, on a line of its own. */}
        {pickupSkip ? <PickupSkipNote skip={pickupSkip} className="basis-full" /> : null}

        {showPrs ? (
          // PrList puts its add button (the icon) before its chips.
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-0.5">
            <PrList task={task} patch={patch} addButton={() => iconButton(TASK_FIELD_LABELS.prs, <GitPullRequest />)} {...picking('prs')} />
          </div>
        ) : null}

        {/* A task taken up from another's suggestion builds on that task's branch (T-14). */}
        {task.stacked_on ? <StackedOn stackedOn={task.stacked_on} onNavigate={onNavigate} className={cn('min-h-7 rounded-full px-2', chipBg)} /> : null}
        {/* A sub-task (T-42): the larger task it's part of. */}
        {task.parent ? <SubtaskOf parent={task.parent} onNavigate={onNavigate} className={cn('min-h-7 rounded-full px-2', chipBg)} /> : null}

        {showLabels ? (
          <Property
            marker={
              <LabelPicker labels={meta.labels} selected={task.label_ids} onChange={(ids) => void patch({ label_ids: ids }, { label_ids: ids })} {...picking('label')}>
                {iconButton('Labels', <Tag />)}
              </LabelPicker>
            }
          >
            {task.label_ids.map((id) => {
              const label = meta.labels.find((l) => l.id === id);
              return label ? <LabelChip key={id} label={label} className="px-2 py-1 text-xs" /> : null;
            })}
          </Property>
        ) : null}

        {tags.length ? (
          // Collected from the title, description and chat; type #something there to add one.
          <Property
            marker={
              <span role="img" aria-label="Tags" data-hint="Tags: type #something in the description or chat" className="flex w-7 shrink-0 justify-center text-muted-foreground [&_svg]:size-3.5">
                <Hash />
              </span>
            }
          >
            {tags.map((tag) => (
              <Link
                key={tag}
                to={boardPath(null, `?tag=${encodeURIComponent(tag)}`)}
                onClick={onNavigate}
                data-hint="Tasks with this tag"
                className={cn('inline-flex h-7 items-center rounded-full px-2 text-sm hover:underline', chipBg)}
              >
                #{tag}
              </Link>
            ))}
          </Property>
        ) : null}

        {showLinks ? (
          <Property
            marker={
              <ContextPicker
                currentPage={currentPage}
                {...picking('link')}
                onPick={(ref) =>
                  void run(addTaskContexts(task.id, [ref]), {
                    contexts: task.contexts.some((c) => c.type === ref.type && c.id === ref.id)
                      ? task.contexts
                      : [...task.contexts, { type: ref.type, id: ref.id, title: ref.title ?? 'Linked', url: '', parent: null, source: 'manual' }],
                  })
                }
              >
                {iconButton('Linked to', <Link2 />)}
              </ContextPicker>
            }
          >
            {task.contexts.map((c) => (
              <ContextChip
                key={`${c.type}:${c.id}`}
                context={c}
                onNavigate={onNavigate}
                onRemove={() =>
                  void run(removeTaskContext(task.id, c), {
                    contexts: task.contexts.filter((x) => !(x.type === c.type && x.id === c.id)),
                  })
                }
              />
            ))}
          </Property>
        ) : null}
        {addable.length ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <AddButton label="Add a property" showLabel={false} data-hint="Add a property" />
            </DropdownMenuTrigger>
            {/* No focus back to the +: the picker that opens takes it. */}
            <DropdownMenuContent
              align="start"
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                if (picked) setAdding(picked);
                setPicked(null);
              }}
            >
              {addable.map((item) => (
                <DropdownMenuItem key={item.what} onSelect={() => setPicked(item.what)}>
                  {item.icon}
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
